import { useCallback, useMemo, useState } from 'react'

import i18n from 'i18n-literally'
import PropTypes from 'prop-types'
import { pick, trim } from 'ramda'

import { CheckCircle as CheckCircleIcon } from '@mui/icons-material'
import { Alert, Box, Chip, LinearProgress } from '@mui/material'
import { styled } from '@mui/material/styles'

import { useApiFetch, useApiQuery } from '~/src/IO/Api'
import getGlobal from '~/src/Lib/getGlobal'
import LoginForm from '~/src/Routes/Login/LoginForm'
import { isValidEmail } from '~/src/Store/bundles/me/shape'
import { useConnectModern } from '~/src/Store/hooks/useConnectModern'
import Button from '~/src/UI/Shared/Button'
import PageTitle from '~/src/UI/Shared/PageTitle'
import T from '~/src/UI/Shared/Typography'

import { sanitizeOAuthAuthorizeParams } from './utils'

const loginPicker = pick(['email', 'password'])
const global = getGlobal()

const ConsentActions = styled('div')(({ theme }) => ({
  display: 'grid',
  gridTemplateColumns: 'repeat(2, minmax(0, 10rem))',
  justifyContent: 'center',
  gap: theme.spacing(2),
  margin: theme.spacing(3, 'auto', 0),
  width: '100%',
}))

const CallbackOrigin = styled('div')(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  gap: theme.spacing(1),
  marginBottom: theme.spacing(1),
}))

const UnverifiedCallbackAlert = styled(Alert)(({ theme }) => ({
  marginTop: theme.spacing(2),
  '& .MuiAlert-icon': { fontSize: theme.typography.h4.fontSize },
  '& .MuiAlert-message': { width: '100%' },
}))

const CallbackUrl = styled('code')(({ theme }) => ({
  display: 'block',
  marginTop: theme.spacing(1),
  overflowWrap: 'anywhere',
  color: theme.palette.text.primary,
}))

export const OAuthConsent = ({ approving, context, onApprove, onCancel, redirectUri }) => {
  const clientIsVerified = context.client_verification === 'verified'

  return (
    <Box>
      <PageTitle
        disablePortal
        titleText={i18n`Authorize AROYA`}
        title={i18n`Connect ${context.client_name}`}
        align="center"
      />
      <Box display="flex" justifyContent="center" marginBottom={2}>
        <Chip
          color={clientIsVerified ? 'success' : 'warning'}
          label={clientIsVerified ? i18n`Verified` : i18n`Unverified`}
          size="small"
        />
      </Box>
      <T align="center" marginBottom={1}>
        {i18n`${context.client_name} is requesting access to AROYA.`}
      </T>
      <CallbackOrigin>
        {clientIsVerified ? <CheckCircleIcon aria-label={i18n`Verified callback origin`} color="success" /> : null}
        <T color="text.secondary">{i18n`Callback origin: ${context.redirect_uri_origin}`}</T>
      </CallbackOrigin>
      {!clientIsVerified ? (
        <UnverifiedCallbackAlert severity="warning" variant="filled">
          <T bold>{i18n`This service is not recognized by AROYA.`}</T>
          <T>{i18n`Only continue if you trust this redirect URL:`}</T>
          <CallbackUrl>{redirectUri}</CallbackUrl>
        </UnverifiedCallbackAlert>
      ) : null}
      <T align="center" color="text.secondary">
        {i18n`Resource: ${context.resource}`}
      </T>
      <ConsentActions>
        <Button fullWidth variant="text" onClick={onCancel} disabled={approving}>
          {i18n`Cancel`}
        </Button>
        <Button fullWidth onClick={onApprove} disabled={approving}>
          {i18n`Authorize`}
        </Button>
      </ConsentActions>
    </Box>
  )
}

OAuthConsent.propTypes = {
  approving: PropTypes.bool.isRequired,
  context: PropTypes.shape({
    client_name: PropTypes.string.isRequired,
    client_verification: PropTypes.oneOf(['unverified', 'verified']).isRequired,
    redirect_uri_origin: PropTypes.string.isRequired,
    resource: PropTypes.string.isRequired,
  }).isRequired,
  onApprove: PropTypes.func.isRequired,
  onCancel: PropTypes.func.isRequired,
  redirectUri: PropTypes.string.isRequired,
}

const OAuthAuthorize = () => {
  const { auth, doForgotClearState, doLogin, doResetClearState, queryObject } = useConnectModern(
    'selectAuth',
    'selectQueryObject',
    'doForgotClearState',
    'doLogin',
    'doResetClearState',
  )
  const apiFetch = useApiFetch()
  // Only sanitized OAuth params are sent back to AROYA BE or used for cancel redirects.
  const authorizationParams = useMemo(() => sanitizeOAuthAuthorizeParams(queryObject), [queryObject])
  const [approvalError, setApprovalError] = useState('')
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [approving, setApproving] = useState(false)

  const loginState = useMemo(() => ({ email, password }), [email, password])
  const isFormValid = Boolean(isValidEmail(email) && trim(password).length)
  const {
    data: context,
    error: contextError,
    isPending: contextIsPending,
  } = useApiQuery({
    path: '/oauth/authorize/context/',
    params: authorizationParams,
    apiOptions: { authenticated: false },
    queryOptions: { enabled: Boolean(authorizationParams) },
  })
  const error =
    approvalError || (authorizationParams ? contextError?.message : i18n`Invalid authorization request.`) || ''

  const setLoginState = useCallback(update => {
    if ('email' in update) setEmail(update.email)
    if ('password' in update) setPassword(update.password)
  }, [])

  const onLoginSubmit = useCallback(
    event => {
      event.preventDefault()
      setApprovalError('')
      doForgotClearState()
      doResetClearState()
      if (isFormValid) {
        doLogin(loginPicker(loginState))
      }
    },
    [doForgotClearState, doLogin, doResetClearState, isFormValid, loginState],
  )

  const onApprove = useCallback(async () => {
    if (!authorizationParams) return
    setApproving(true)
    setApprovalError('')
    try {
      const response = await apiFetch('/oauth/authorize/approve/', authorizationParams, { method: 'POST' })
      global.location.assign(response.redirectUri)
    } catch (approveError) {
      setApproving(false)
      setApprovalError(approveError.message || i18n`Unable to authorize this client.`)
    }
  }, [apiFetch, authorizationParams])

  const onCancel = useCallback(() => {
    if (!authorizationParams) return
    const redirectUri = new URL(authorizationParams.redirect_uri)
    redirectUri.searchParams.set('error', 'access_denied')
    if (authorizationParams.state) {
      redirectUri.searchParams.set('state', authorizationParams.state)
    }
    global.location.assign(redirectUri.toString())
  }, [authorizationParams])

  return (
    <Box
      className="aroya__no-auto-height"
      display="flex"
      minHeight="100dvh"
      justifyContent="center"
      alignItems="center"
      padding={2}
    >
      <Box width="100%" maxWidth={520}>
        <Box display="flex" justifyContent="center" marginBottom={3}>
          <Box component="img" src="/images/aroya-logo-large.svg" alt="AROYA logo" width="10rem" />
        </Box>

        {error ? (
          <Alert severity="error" variant="outlined" sx={{ marginBottom: 2 }}>
            {error}
          </Alert>
        ) : null}
        {auth.loginFailed ? (
          <Alert severity="error" variant="outlined" sx={{ marginBottom: 2 }}>
            {auth.loginMessage}
          </Alert>
        ) : null}

        {authorizationParams && contextIsPending ? <LinearProgress variant="indeterminate" color="primary" /> : null}

        {context && !auth.authenticated ? (
          <LoginForm
            alreadyHasAccount
            email={email}
            isFormValid={isFormValid}
            loginProcessing={auth.loginProcessing}
            onSubmit={onLoginSubmit}
            password={password}
            setState={setLoginState}
            token={false}
          />
        ) : null}

        {context && auth.authenticated ? (
          <OAuthConsent
            approving={approving}
            context={context}
            onApprove={onApprove}
            onCancel={onCancel}
            redirectUri={authorizationParams.redirect_uri}
          />
        ) : null}
      </Box>
    </Box>
  )
}

export default OAuthAuthorize
